<template>
  <!-- Mobile: disclosure reservation bloc -->
  <DbDisclosure
    class="db-disclosure-bloc db-disclosure-reservation"
    :expanded="isResevationExpanded"
    v-on:click="isResevationExpanded = !isResevationExpanded"
  >
    <template v-slot:button>
      <span>{{ $t('Your reservation') }}</span>
      <IconDbChevron
        :class="{
          'db-rotate-90': isResevationExpanded,
          'db-rotate-270': !isResevationExpanded,
        }"
        width="14"
        height="14"
      />
    </template>
    <FunnelReservationSummary />
  </DbDisclosure>

  <!-- Page header -->
  <div class="db-funnel-header">
    <!-- Page title -->
    <h1 class="db-funnel-title db-hotelsearch-title db-title-3">
      {{ $t('Checkout') }}
    </h1>
  </div>

  <div
    id="db-results"
    class="db-funnel-results db-checkout-results"
  >
    <div class="db-checkout-results-content">
      <!-- Desktop: reservation bloc -->
      <DbCardGroup
        fullContent
        variant="sm"
        class="db-group-reservation"
      >
        <template v-slot:title>
          {{ $t('Your reservation') }}
        </template>
        <div class="db-tw-pt-1">
          <FunnelReservationSummary />
        </div>
      </DbCardGroup>
      <!-- !Desktop: reservation bloc -->

      <CheckoutForm v-on:checkout="checkout">
        <!-- Total block only displayed on mobile -->
        <p class="db-group-payment-total">
          <span class="db-group-payment-total-text db-title-2">{{ $t('Total') }}</span>
          <span class="db-group-payment-total-subtext db-text-small">
            {{ $t('Taxes included') }}
          </span>
          <span class="db-group-payment-total-price db-title-1 db-text-right">{{
            cartSummary?.formattedPricing?.totalReservation
          }}</span>
          <DbDepositTable class="db-group-payment-total-deposit" />
        </p>
      </CheckoutForm>
    </div>
    <!-- !.db-checkout-results-content -->
  </div>
  <!-- !.db-checkout-results -->
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

import FunnelReservationSummary from '@/AppBookingFunnel/components/FunnelReservationSummary.vue'

import { useCartStore } from '@/stores/cart'
import { useConfigurations } from '@/stores/configurations'
import { useGtm } from '@/composables/features/useGtm'
import { usePackagesStore } from '@/stores/packages'

const { event, eventData } = useGtm()

const cartStore = useCartStore()
let { cartSummary } = cartStore

const hotel = window.db2BookingFunnel.getHotel()

/** Checkout form has been validated */
async function checkout() {
  useConfigurations().general.warnUnload = false
  if (cartStore.options.needGuestrooms)
    eventData('user_behavior', {
      event_source: 'gaEventDelphiDirect',
      behavior_type: 'guestrooms_needed',
    })
  await cartStore.book()
}

/** Reservation bloc is a disclosure on mobile */
const isResevationExpanded = ref(false)

onMounted(() => {
  const packagesStore = usePackagesStore()
  let { cartSummary } = cartStore
  let attendees_bucket = ''
  if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
  if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
  if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
  if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
  if (packagesStore.attendees > 30) attendees_bucket = '+30'
  let total_tax =
    parseFloat(cartSummary?.price?.taxes || '0') +
    parseFloat(cartSummary?.price?.gratuityFee || '0') +
    parseFloat(cartSummary?.price?.adminFee || '0')

  event('begin_checkout', {
    currency: cartSummary?.currencyCode,
    value: (parseFloat(cartSummary?.price?.totalReservation || '0') - total_tax).toFixed(2),
    coupon: '',
    items: [
      {
        item_id: cartSummary?.db2PackageId,
        item_name: cartSummary?.packageName,
        affiliation: hotel?.name,
        coupon: '',
        discount: 0,
        index: 0,
        item_brand: hotel?.master_item?.name,
        item_category: 'meeting rooms packages',
        item_variant: '',
        price: parseFloat(cartSummary?.price?.packageBundle || '0').toFixed(2),
        attendees_bucket: attendees_bucket,
        quantity: 1,
      },
    ],
  })
})
</script>

<style>
@import '@/assets/styles/components/app-containers/_checkout.css';
@import '@/assets/styles/components/modals/_modal-tax-fee.css';
</style>
